import { useMemo, useState, type ChangeEvent, type CSSProperties, type ReactNode, type WheelEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, ConflictError } from '../api/client';
import type { DriverStint, ShopVisit, UnitRecord, UnitWrite } from '../api/types';
import { StageText, VisitForm, money } from '../components/ShopForms';
import { useApp } from '../state/AppContext';
import {
  ConflictBanner, EmployeePicker, ErrorBanner, HistoryPanel, Select, formatDateTime, formatLongDate, today,
} from '../components/ui';
import { StatusText } from './Register';

/**
 * One unit: its record, and the form that changes it. The same page adds a new
 * unit (/units/new), where it opens straight on the form.
 */
export function UnitPage() {
  const { id } = useParams();
  const isNew = id === undefined;
  const record = useQuery({
    queryKey: ['unit', id],
    queryFn: () => api.get<UnitRecord>(`/api/vehicles/${id}`),
    enabled: !isNew,
  });

  if (isNew) return <UnitForm />;
  if (record.isLoading) return <div className="spinner-note">Loading the unit…</div>;
  if (record.isError || !record.data) {
    return <ErrorBanner message={record.error ? (record.error as Error).message : 'That unit could not be loaded.'} />;
  }
  return <UnitView key={record.data.rowVersionNo} record={record.data} />;
}

/* ---------------------------------------------------------------------------
   The record
   --------------------------------------------------------------------------- */

function UnitView({ record }: { record: UnitRecord }) {
  const { reference, statusLabel, ownershipLabel, originName } = useApp();
  // The register's Edit button opens the unit straight on its form.
  const [params, setParams] = useSearchParams();
  const [editing, setEditingState] = useState(params.has('edit') && record.canEdit);
  const setEditing = (on: boolean) => {
    setEditingState(on);
    if (!on && params.has('edit')) setParams({}, { replace: true });
  };
  const [showSource, setShowSource] = useState(false);
  const queryClient = useQueryClient();
  const u = record.unit;
  const cls = reference.classes.find((c) => c.classCode === u.classCode);

  const clear = useMutation({
    mutationFn: () => api.put(`/api/vehicles/${u.fleetAssetId}`, { ...toWrite(record), needsReview: false, reviewReasons: [] }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['unit'] });
      void queryClient.invalidateQueries({ queryKey: ['units'] });
    },
  });

  if (editing) return <UnitForm record={record} onDone={() => setEditing(false)} />;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">{u.unitNumber ?? u.vin ?? 'Unit'}</div>
          <div className="page-subtitle">
            {[u.modelYear, u.make, u.model].filter(Boolean).join(' ') || u.className} · {u.className} ·{' '}
            <StatusText status={u.status} label={statusLabel(u.status)} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Link className="btn btn--sm" to="/register">All units</Link>
          {record.canEdit && <button className="btn btn--sm btn--primary" onClick={() => setEditing(true)}>Edit</button>}
        </div>
      </div>

      {u.needsReview && (
        <div className="banner banner--conflict" style={{ alignItems: 'flex-start' }}>
          <div>
            <strong>Needs review.</strong>
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {u.reviewReasons.map((r) => <li key={r}>{r}</li>)}
              {u.reviewReasons.length === 0 && <li>Flagged by hand.</li>}
            </ul>
          </div>
          {record.can.edit && (
            <button className="btn btn--sm" disabled={clear.isPending} onClick={() => clear.mutate()}
              title="Everything above has been checked, and the record is right as it stands">
              {clear.isPending ? 'Saving…' : 'Mark as reviewed'}
            </button>
          )}
        </div>
      )}
      {clear.isError && <ErrorBanner message={(clear.error as Error).message} />}

      <div style={{ display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))' }}>
        <Card title="WHAT IT IS">
          <Field label="Name" value={u.unitNumber} />
          <Field label="VIN" value={u.vin} />
          <Field label="Kind" value={u.className} />
          <Field label="Year, make, model" value={[u.modelYear, u.make, u.model].filter(Boolean).join(' ')} />
          <Field label="Body" value={u.bodyType} />
          <Field label="Fuel" value={u.fuel} />
          {cls?.fields.map((f) => <Field key={f.key} label={f.label} value={record.classFields[f.key]} />)}
        </Card>
        <Card title="REGISTRATION">
          <Field label="Plate" value={u.plateNumber ? `${u.plateState ?? '(no state)'} ${u.plateNumber}` : null} />
          <Field label="Registration expires" value={u.registrationExpiresOn ? formatLongDate(u.registrationExpiresOn) : null} />
          <Field label="FMCSA inspection" value={u.fmcsaInspectedOn ? formatLongDate(u.fmcsaInspectedOn) : null} />
        </Card>
        <Card title="WHO OWNS IT">
          <Field label="Origin" value={originName(u.originCode)} />
          <Field label="Ownership" value={u.ownership ? ownershipLabel(u.ownership) : null} />
          <Field label={u.ownership === 'owned' ? 'Owner' : 'Vendor or lender'} value={u.vendor} />
          <Field label="Vendor's unit number" value={u.vendorUnitNumber} />
          {endsLabel(u.ownership) && (
            <Field label={endsLabel(u.ownership)!} value={u.termEndsOn ? formatLongDate(u.termEndsOn) : null} />
          )}
          <Field label="Received" value={u.receivedOn ? formatLongDate(u.receivedOn) : null} />
        </Card>
        <Card title="WHERE IT WORKS">
          <Field label="Status" value={statusLabel(u.status)} />
          <Field label="Region" value={u.region} />
          <Field label="Home location" value={u.home} />
          <Field label="Route" value={u.routeCode} />
          <Field label="Assignment" value={u.assignment} />
          <Field label="Driver" value={u.driver ? `${u.driver}${u.driverSince ? ` · since ${formatLongDate(u.driverSince)}` : ''}` : null} />
          {cls?.hasOdometer && <Field label="Odometer" value={u.odometer != null ? `${u.odometer.toLocaleString()} mi` : null} />}
          {cls?.hasEngineHours && <Field label="Engine hours" value={u.engineHours?.toLocaleString()} />}
        </Card>
      </div>

      {record.drivers.length > 0 && (
        <Card title="DRIVERS" style={{ marginTop: 14 }}>
          {record.drivers.map((d) => (
            <div key={d.driverAssignmentId}
              style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 12px', padding: '4px 0', borderBottom: '1px solid var(--rule)', fontSize: 12.5 }}>
              <div style={{ width: 200, flex: 'none', color: 'var(--text)', fontWeight: d.endsOn ? 400 : 600 }}>{d.name}</div>
              <div style={{ color: 'var(--text-secondary)' }}>{stintDates(d)}</div>
            </div>
          ))}
        </Card>
      )}

      {(record.shopVisits.length > 0 || record.can.shops) && (
        <ShopVisitsCard record={record} />
      )}

      {record.notes && (
        <Card title="NOTES" style={{ marginTop: 14 }}>
          <div style={{ whiteSpace: 'pre-wrap', fontSize: 12.5, color: 'var(--text-secondary)' }}>{record.notes}</div>
        </Card>
      )}

      {record.source && (
        <div className="card" style={{ marginTop: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div className="section-label" style={{ marginBottom: 0 }}>FROM THE SPREADSHEET</div>
            <button className="btn btn--sm" onClick={() => setShowSource((v) => !v)}>
              {showSource ? 'Hide' : `Show row ${record.source.Row ?? ''} as it was`}
            </button>
          </div>
          {showSource && (
            <table style={{ borderCollapse: 'collapse', fontSize: 12.5, marginTop: 10 }}>
              <tbody>
                {Object.entries(record.source).filter(([k]) => k !== 'Row').map(([k, v]) => (
                  <tr key={k}>
                    <td style={{ padding: '2px 16px 2px 0', color: 'var(--label)', fontWeight: 600, whiteSpace: 'nowrap', verticalAlign: 'top' }}>{k}</td>
                    <td style={{ padding: '2px 0', color: 'var(--text-secondary)' }}>{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div className="card" style={{ marginTop: 14 }}>
        <HistoryPanel rows={record.history} loading={false} />
        <div className="meta" style={{ marginTop: 8 }}>
          Added {formatDateTime(record.createdAtUtc)}
          {record.updatedAtUtc && <> · last changed {formatDateTime(record.updatedAtUtc)}</>}
          {' '}· the full before and after of every change is in Admin's Audit Log, under Fleet.
        </div>
      </div>
    </div>
  );
}

/**
 * This unit's trips to repair shops, open ones first. Each visit opens to move
 * it along — estimate, approval or denial, pick-up — for anyone who may log them.
 */
function ShopVisitsCard({ record }: { record: UnitRecord }) {
  const [editing, setEditing] = useState<ShopVisit | 'new' | null>(null);
  const u = record.unit;
  return (
    <div className="card" style={{ marginTop: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
        <div className="section-label" style={{ marginBottom: 0 }}>SHOP VISITS</div>
        {record.can.shops && (
          <button className="btn btn--sm" style={{ marginLeft: 'auto' }} onClick={() => setEditing('new')}>Send to a shop</button>
        )}
      </div>
      {record.shopVisits.length === 0 && <div className="meta">No shop visits logged.</div>}
      {record.shopVisits.map((v) => (
        <div key={v.shopVisitId}
          onClick={() => v.canEdit && setEditing(v)}
          className={v.canEdit ? 'clickable' : undefined}
          style={{ padding: '6px 0', borderBottom: '1px solid var(--rule)', fontSize: 12.5, cursor: v.canEdit ? 'pointer' : undefined }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 12px', alignItems: 'baseline' }}>
            <div style={{ width: 200, flex: 'none', color: 'var(--text)', fontWeight: v.pickedUpOn ? 400 : 600 }}>{v.shopName}</div>
            <div style={{ color: 'var(--text-secondary)' }}>
              {v.inOn ? `In ${formatLongDate(v.inOn)}` : 'Date in not recorded'}
              {v.pickedUpOn && ` · picked up ${formatLongDate(v.pickedUpOn)}`}
            </div>
            <div style={{ marginLeft: 'auto' }}><StageText stage={v.stage} /></div>
          </div>
          {(v.issues || v.estimate != null) && (
            <div style={{ color: 'var(--text-secondary)', marginTop: 2 }}>
              {v.issues}{v.issues && v.estimate != null ? ' · ' : ''}{v.estimate != null && `estimate ${money(v.estimate)}`}
              {(v.approvedOn || v.deniedOn) && ` · ${v.approvedOn ? 'approved' : 'denied'} ${formatLongDate((v.approvedOn ?? v.deniedOn)!)}${v.decidedBy ? ` by ${v.decidedBy}` : ''}`}
            </div>
          )}
        </div>
      ))}
      {editing && (
        <VisitForm visit={editing === 'new' ? undefined : editing} unitId={u.fleetAssetId} onClose={() => setEditing(null)} />
      )}
    </div>
  );
}

/**
 * A titled block of the record or the form. Locked, its boxes are read-only
 * and it says why: the person's access does not cover that part of the unit.
 */
function Card({ title, children, style, locked }: {
  title: string; children: ReactNode; style?: CSSProperties; locked?: string | false;
}) {
  return (
    <div className="card" style={style}>
      <div className="section-label">
        {title}
        {locked && <span style={{ fontWeight: 500, letterSpacing: 0, marginLeft: 8 }}>· {locked}</span>}
      </div>
      {locked
        ? <fieldset disabled style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>{children}</fieldset>
        : children}
    </div>
  );
}

function Field({ label, value }: { label: string; value?: string | number | null }) {
  const blank = value === null || value === undefined || value === '' || value === '—';
  return (
    <div style={{ display: 'flex', gap: 12, padding: '4px 0', borderBottom: '1px solid var(--rule)', fontSize: 12.5 }}>
      <div style={{ width: 150, flex: 'none', color: 'var(--label)' }}>{label}</div>
      <div style={{ color: blank ? 'var(--muted)' : 'var(--text)' }}>
        {blank ? '—' : value}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------------
   The form
   --------------------------------------------------------------------------- */

function toWrite(r: UnitRecord): UnitWrite {
  const u = r.unit;
  return {
    unitNumber: u.unitNumber, vin: u.vin, classCode: u.classCode, originCode: u.originCode, status: u.status,
    needsReview: u.needsReview, reviewReasons: u.reviewReasons,
    modelYear: u.modelYear, make: u.make, model: u.model, bodyType: u.bodyType, fuel: u.fuel,
    plateNumber: u.plateNumber, plateState: u.plateState,
    ownership: u.ownership, vendor: u.vendor, vendorUnitNumber: u.vendorUnitNumber, termEndsOn: u.termEndsOn,
    regionCode: u.regionCode, homeAssetId: u.homeAssetId, routeAssetId: u.routeAssetId, assignment: u.assignment,
    driverEmployeeId: u.driverEmployeeId,
    odometer: u.odometer, engineHours: u.engineHours, registrationExpiresOn: u.registrationExpiresOn,
    fmcsaInspectedOn: u.fmcsaInspectedOn, receivedOn: u.receivedOn,
    classFields: r.classFields, notes: r.notes, rowVersionNo: r.rowVersionNo,
  };
}

const EMPTY: UnitWrite = {
  classCode: 'vehicle', status: 'active', needsReview: false, reviewReasons: [], classFields: {}, originCode: 'cyntox',
};

function UnitForm({ record, onDone }: { record?: UnitRecord; onDone?: () => void }) {
  const { reference, can } = useApp();
  // What this person may change. On an existing unit the API says, for its
  // region; a new one is checked again on save against the region picked.
  const may = record?.can ?? {
    edit: can('vehicles', 'edit'), drivers: can('vehicles', 'drivers'), ownership: can('vehicles', 'ownership'),
  };
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<UnitWrite>(() => (record ? toWrite(record) : EMPTY));
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);
  const cls = useMemo(() => reference.classes.find((c) => c.classCode === form.classCode), [reference.classes, form.classCode]);

  const set = <K extends keyof UnitWrite>(key: K, value: UnitWrite[K]) => setForm((f) => ({ ...f, [key]: value }));
  const text = (key: keyof UnitWrite) => ({
    value: (form[key] as string | null | undefined) ?? '',
    onChange: (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      set(key, (e.target.value || null) as never),
  });
  const number = (key: 'modelYear' | 'odometer' | 'engineHours', whole = false) => ({
    type: 'number' as const, min: 0, step: whole ? 1 : 0.1,
    value: form[key] ?? '',
    onWheel: (e: WheelEvent<HTMLInputElement>) => e.currentTarget.blur(),
    onChange: (e: ChangeEvent<HTMLInputElement>) => set(key, e.target.value === '' ? null : Number(e.target.value)),
  });

  // A driver is an employee. One driver per unit; one person may still drive
  // two units (a truck and its sub), so that is said rather than refused.
  const pickable = useMemo(
    () => reference.people.filter((p) => p.isActive || p.employeeId === record?.unit.driverEmployeeId),
    [reference.people, record],
  );
  const driverChanged = (form.driverEmployeeId ?? null) !== (record?.unit.driverEmployeeId ?? null);
  const picked = reference.people.find((p) => p.employeeId === form.driverEmployeeId);
  const others = picked?.drives.filter((d) => d.fleetAssetId !== record?.unit.fleetAssetId) ?? [];
  const alsoDrives = picked && others.length > 0
    ? `${picked.displayName} also drives ${others.map((d) => d.name).join(', ')}`
    : null;
  // Region and home come from Admin. A home is in a region, so picking one sets
  // the region, and changing the region drops a home that isn't in it — the
  // same rule the API applies, shown as it happens.
  const homes = reference.homes.filter((h) =>
    (h.isActive || h.assetId === record?.unit.homeAssetId) && (!form.regionCode || h.regionCode === form.regionCode));
  const setRegion = (regionCode: string | null) => setForm((f) => ({
    ...f,
    regionCode,
    homeAssetId: reference.homes.find((h) => h.assetId === f.homeAssetId)?.regionCode === regionCode ? f.homeAssetId : null,
  }));
  const setHome = (homeAssetId: number | null) => setForm((f) => ({
    ...f,
    homeAssetId,
    regionCode: reference.homes.find((h) => h.assetId === homeAssetId)?.regionCode ?? f.regionCode,
  }));

  // The API applies the same rule on save; doing it here shows it as it happens.
  // Leaving "owned" clears the Cyntox it filled in, so the real lender can go in.
  const owned = form.ownership === 'owned';
  const setOwnership = (ownership: string | null) => setForm((f) => ({
    ...f,
    ownership,
    vendor: ownership === 'owned' ? 'Cyntox' : f.ownership === 'owned' ? null : f.vendor,
    termEndsOn: ownership === 'owned' ? null : f.termEndsOn,
  }));

  const vin = (form.vin ?? '').toUpperCase();
  const vinHint = vin && vin.length !== 17 ? `${vin.length} of 17 characters` : vin && /[IOQ]/.test(vin) ? 'A VIN never uses I, O or Q' : null;

  const save = useMutation({
    mutationFn: () => {
      const body = { ...form, vin: form.vin?.toUpperCase() ?? null };
      return record
        ? api.put(`/api/vehicles/${record.unit.fleetAssetId}`, body)
        : api.post<{ fleetAssetId: number }>('/api/vehicles', body);
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['units'] });
      void queryClient.invalidateQueries({ queryKey: ['unit'] });
      void queryClient.invalidateQueries({ queryKey: ['reference'] });
      if (record) onDone?.();
      else navigate(`/units/${(result as { fleetAssetId: number }).fleetAssetId}`);
    },
    onError: (e: Error) => (e instanceof ConflictError ? setConflict(e.message) : setError(e.message)),
  });

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">{record ? `Edit ${record.unit.unitNumber ?? record.unit.vin ?? 'unit'}` : 'Add a unit'}</div>
          <div className="page-subtitle">A name or a VIN is required; everything else can be filled in as it is known.</div>
        </div>
      </div>

      {conflict && <ConflictBanner message={conflict} onReload={() => { setConflict(null); onDone?.(); void queryClient.invalidateQueries({ queryKey: ['unit'] }); }} />}
      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      <div style={{ display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))' }}>
        <Card title="WHAT IT IS" locked={!may.edit && LOCKED}>
          <FormRow label="Kind">
            <Select className="control" value={form.classCode} onChange={(e) => set('classCode', e.target.value)}>
              {reference.classes.filter((c) => c.isActive || c.classCode === form.classCode).map((c) => (
                <option key={c.classCode} value={c.classCode}>{c.name}</option>
              ))}
            </Select>
          </FormRow>
          <FormRow label="Name"><input className="control" {...text('unitNumber')} /></FormRow>
          <FormRow label="VIN" hint={vinHint}>
            <input className="control" style={{ width: 210 }} maxLength={17} {...text('vin')}
              onChange={(e) => set('vin', e.target.value.toUpperCase().replace(/\s/g, '') || null)} />
          </FormRow>
          <FormRow label="Year"><input className="control" style={{ width: 90 }} {...number('modelYear', true)} /></FormRow>
          <FormRow label="Make"><input className="control" {...text('make')} /></FormRow>
          <FormRow label="Model"><input className="control" {...text('model')} /></FormRow>
          <FormRow label="Body"><input className="control" placeholder="Van, 26', Dry van…" {...text('bodyType')} /></FormRow>
          <FormRow label="Fuel">
            <Select className="control" {...text('fuel')}>
              <option value="">—</option>
              <option value="diesel">Diesel</option>
              <option value="gasoline">Gasoline</option>
            </Select>
          </FormRow>
          {cls?.fields.map((f) => (
            <FormRow key={f.key} label={f.label}>
              <input className="control" value={form.classFields[f.key] ?? ''}
                onChange={(e) => set('classFields', { ...form.classFields, [f.key]: e.target.value || null })} />
            </FormRow>
          ))}
        </Card>

        <Card title="REGISTRATION" locked={!may.edit && LOCKED}>
          {cls?.hasPlate !== false && (
            <FormRow label="Plate">
              <div style={{ display: 'flex', gap: 6 }}>
                <input className="control" style={{ width: 60 }} maxLength={2} placeholder="ST" aria-label="Plate state"
                  value={form.plateState ?? ''} onChange={(e) => set('plateState', e.target.value.toUpperCase() || null)} />
                <input className="control" style={{ width: 140, minWidth: 0, flex: '0 1 140px' }} aria-label="Plate number" {...text('plateNumber')} />
              </div>
            </FormRow>
          )}
          <FormRow label="Registration expires"><input type="date" className="control" {...text('registrationExpiresOn')} /></FormRow>
          <FormRow label="FMCSA inspection"><input type="date" className="control" {...text('fmcsaInspectedOn')} /></FormRow>
        </Card>

        <Card title="WHO OWNS IT" locked={!may.ownership && LOCKED}>
          <FormRow label="Origin">
            <Select className="control" {...text('originCode')}>
              <option value="">—</option>
              {reference.origins.map((o) => <option key={o.originCode} value={o.originCode}>{o.name}</option>)}
            </Select>
          </FormRow>
          <FormRow label="Ownership">
            <Select className="control" value={form.ownership ?? ''} onChange={(e) => setOwnership(e.target.value || null)}>
              <option value="">—</option>
              {reference.ownerships.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
            </Select>
          </FormRow>
          {owned ? (
            <FormRow label="Owner"><div style={{ fontSize: 12.5, color: 'var(--text)', lineHeight: '32px' }}>Cyntox</div></FormRow>
          ) : (
            <FormRow label="Vendor or lender"><input className="control" {...text('vendor')} /></FormRow>
          )}
          <FormRow label="Vendor's unit number"><input className="control" {...text('vendorUnitNumber')} /></FormRow>
          {endsLabel(form.ownership) && (
            <FormRow label={endsLabel(form.ownership)!}><input type="date" className="control" {...text('termEndsOn')} /></FormRow>
          )}
          <FormRow label="Received"><input type="date" className="control" {...text('receivedOn')} /></FormRow>
        </Card>

        <Card title="WHERE IT WORKS" locked={!may.edit && LOCKED}>
          <FormRow label="Status">
            <Select className="control" value={form.status} onChange={(e) => set('status', e.target.value)}>
              {reference.statuses.map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}
            </Select>
          </FormRow>
          <FormRow label="Region">
            <Select className="control" value={form.regionCode ?? ''} onChange={(e) => setRegion(e.target.value || null)}>
              <option value="">—</option>
              {reference.regions.map((r) => <option key={r.regionCode} value={r.regionCode}>{r.name}</option>)}
            </Select>
          </FormRow>
          <FormRow label="Home location" hint="The facility or storage unit it is based at, if it has one">
            <Select className="control" value={form.homeAssetId ?? ''}
              onChange={(e) => setHome(e.target.value ? Number(e.target.value) : null)}>
              <option value="">—</option>
              {homes.map((h) => <option key={h.assetId} value={h.assetId}>{h.code}</option>)}
            </Select>
          </FormRow>
          <FormRow label="Route" hint="The Inventory route it runs, if it runs one">
            <Select className="control" value={form.routeAssetId ?? ''}
              onChange={(e) => set('routeAssetId', e.target.value ? Number(e.target.value) : null)}>
              <option value="">—</option>
              {reference.routes.filter((r) => r.isActive || r.assetId === form.routeAssetId).map((r) => (
                <option key={r.assetId} value={r.assetId}>{r.code}</option>
              ))}
            </Select>
          </FormRow>
          <FormRow label="Assignment"><input className="control" {...text('assignment')} /></FormRow>
          {cls?.hasOdometer && <FormRow label="Odometer (mi)"><input className="control" style={{ width: 130 }} {...number('odometer')} /></FormRow>}
          {cls?.hasEngineHours && <FormRow label="Engine hours"><input className="control" style={{ width: 130 }} {...number('engineHours')} /></FormRow>}
        </Card>

        {/* Its own card, because changing drivers is its own permission: a
            person who hands vans out need not be able to edit the rest. */}
        <Card title="DRIVER" locked={!may.drivers && LOCKED}>
          <FormRow label="Driver" hint={alsoDrives}>
            <EmployeePicker id="unit-driver" people={pickable} value={form.driverEmployeeId ?? null}
              placeholder="Search employees by name or ID…"
              // Null with text still in the box is a search in progress, not a clear.
              onChange={(employeeId, typed) => { if (employeeId !== null || typed === '') set('driverEmployeeId', employeeId); }} />
          </FormRow>
          {driverChanged && (
            <FormRow label={form.driverEmployeeId ? 'Driving from' : 'Off the unit from'}
              hint={record?.unit.driver ? `${record.unit.driver} stops driving it that day` : null}>
              <input type="date" className="control" value={form.driverFrom ?? today()}
                min={record?.unit.driverSince ?? undefined}
                onChange={(e) => set('driverFrom', e.target.value || null)} />
            </FormRow>
          )}
        </Card>
      </div>

      <Card title="NOTES" style={{ marginTop: 14 }} locked={!may.edit && LOCKED}>
        <textarea className="control" rows={3} style={{ width: '100%' }} {...text('notes')} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, marginTop: 10, color: 'var(--text-secondary)' }}>
          <input type="checkbox" checked={form.needsReview}
            onChange={(e) => setForm((f) => ({ ...f, needsReview: e.target.checked, reviewReasons: e.target.checked ? f.reviewReasons : [] }))} />
          Needs review
          {form.needsReview && form.reviewReasons.length > 0 && <span className="meta"> · {form.reviewReasons.length} reasons kept</span>}
        </label>
      </Card>

      <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
        <button className="btn btn--primary" disabled={save.isPending} onClick={() => { setError(null); save.mutate(); }}>
          {save.isPending ? 'Saving…' : record ? 'Save changes' : 'Add unit'}
        </button>
        <button className="btn" onClick={() => (record ? onDone?.() : navigate('/register'))}>Cancel</button>
      </div>
    </div>
  );
}


/** "Since Oct 2, 2026", "Oct 2 – Oct 9, 2026", or from before the register began. */
/** What a locked part of the form says about itself. */
const LOCKED = 'your access does not cover this';

function stintDates(d: DriverStint): string {
  const from = d.startsOn ? formatLongDate(d.startsOn) : 'Before the register';
  if (!d.endsOn) return d.startsOn ? `Since ${from}` : 'Since before the register';
  return `${from} – ${formatLongDate(d.endsOn)}`;
}

/**
 * What the end date is called for each kind of ownership, or null where
 * nothing ends: an owned unit, or one whose ownership is not known.
 */
function endsLabel(ownership?: string | null): string | null {
  switch (ownership) {
    case 'financed': return 'Financing ends';
    case 'operating_lease':
    case 'financed_lease': return 'Lease ends';
    case 'rental_long':
    case 'rental_short': return 'Rental ends';
    default: return null;
  }
}

function FormRow({ label, hint, children }: { label: string; hint?: string | null; children: ReactNode }) {
  return (
    <div className="form-row">
      <label className="field-label form-row__label">{label.toUpperCase()}</label>
      <div className="form-row__value">
        {children}
        {hint && <div className="meta" style={{ marginTop: 2 }}>{hint}</div>}
      </div>
    </div>
  );
}
